import React, { FC, useState, useEffect } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { useChatGraphQL } from './hooks/useChatGraphQL'
import { ConversationList } from './components/ConversationList'
import { ChatWindow } from './components/ChatWindow'
import { NewChatModal, ContactSearchResult as GraphQLContactResult } from './components/NewChatModal'
import { SendMessageCanvas } from '../../components/messaging/SendMessageCanvas'
import { ContactSearchResult } from './hooks/useChatGraphQL'
import { parseId } from '../../helpers'
import {useIntl} from 'react-intl'
import {CHAT_MESSAGES as C} from './chatMessages'

const InboxPage: FC = () => {
  const intl = useIntl()
    const {
        conversations,
        currentConversation,
        setCurrentConversation,
        messages,
        loadingInbox,
        loadingMessages,
        sendMessage,
        sendMessageWithMedia,
        startConversation,
        inboxMode,
        setInboxMode,
        isAdmin,
        isSupervisor,
        isSuperAdmin,
        // Filters
        filterUnread, setFilterUnread,
        filterTags, setFilterTags,
        filterDateFrom, setFilterDateFrom,
        filterDateTo, setFilterDateTo,
        setFilterSearch,
        filterUserId, setFilterUserId,
        filterOrgId, setFilterOrgId,
        orgOptions,
        fetchOrgOptions,
        availableTags,
        createTag,
        manageTag,
        configSource,
        availableConfigs,
        inboxUsers,
        fetchInboxUsers,
        reloadInbox,
        // Pagination
        currentPage,
        setCurrentPage,
        pageSize,
        totalConversations
    } = useChatGraphQL()

    const [showNewChat, setShowNewChat] = useState(false)
    const [showSendMessage, setShowSendMessage] = useState(false)
    const [selectedRecipient, setSelectedRecipient] = useState<ContactSearchResult | null>(null)

    // Client-side channel filters (fast, no backend trip needed)
    const [filterWhatsapp, setFilterWhatsapp] = useState(false)
    const [filterSms, setFilterSms] = useState(false)

    // Raw input — debounced 500 ms into the backend search param
    const [rawSearch, setRawSearch] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilterSearch(rawSearch)
            setCurrentPage(0)
        }, 500)
        return () => clearTimeout(timer)
    }, [rawSearch, setFilterSearch, setCurrentPage])

    // Only client-side channel filter remains; name/phone search is server-side
    const filteredConversations = conversations.filter(c => {
        const hasWhatsapp = c.active_channels?.includes('WHATSAPP') || c.last_message?.channel === 'WHATSAPP'
        const hasSms = c.active_channels?.includes('SMS') || c.last_message?.channel === 'SMS'
        if (filterWhatsapp && !hasWhatsapp) return false
        if (filterSms && !hasSms) return false
        return true
    })

    // Map GraphQL result (camelCase) to local format (snake_case)
    const handleContactSelected = (contact: GraphQLContactResult) => {
        const mappedContact: ContactSearchResult = {
            id: Number(parseId(contact.id)), // Decode Relay Global ID (base64) to numeric PK
            full_name: contact.fullName,
            phone: contact.phone,
            email: contact.email
        }
        setSelectedRecipient(mappedContact)
        setShowNewChat(false)
        setShowSendMessage(true)
    }

    const handleMessageSent = () => {
        if (selectedRecipient) {
            startConversation(selectedRecipient)
        }
        setShowSendMessage(false)
    }


    return (
        <>
            <PageTitle breadcrumbs={[]}>{intl.formatMessage(C.inbox)}</PageTitle>

            <div className='d-flex flex-column flex-lg-row inbox-page'>
                {/* Sidebar */}
                <div className='flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-10 mb-lg-0'>
                    <ConversationList
                        conversations={filteredConversations}
                        selectedId={currentConversation?.id}
                        onSelect={setCurrentConversation}
                        onNewChat={() => setShowNewChat(true)}
                        onRefresh={reloadInbox}
                        loading={loadingInbox}
                        searchQuery={rawSearch}
                        onSetSearchQuery={setRawSearch}
                        inboxMode={inboxMode}
                        onSetMode={setInboxMode}
                        isAdmin={isAdmin}
                        isSupervisor={isSupervisor}
                        filterWhatsapp={filterWhatsapp}
                        onToggleWhatsapp={setFilterWhatsapp}
                        filterSms={filterSms}
                        onToggleSms={setFilterSms}

                        filterUnread={filterUnread}
                        onToggleUnread={setFilterUnread}
                        filterDateFrom={filterDateFrom}
                        onSetDateFrom={setFilterDateFrom}
                        filterDateTo={filterDateTo}
                        onSetDateTo={setFilterDateTo}
                        filterTags={filterTags}
                        onSetTags={setFilterTags}
                        availableTags={availableTags}
                        filterUserId={filterUserId}
                        onSetFilterUserId={(id) => { setFilterUserId(id); setCurrentPage(0) }}
                        inboxUsers={inboxUsers}
                        onFetchInboxUsers={fetchInboxUsers}
                        isSuperAdmin={isSuperAdmin}
                        filterOrgId={filterOrgId}
                        onSetFilterOrgId={(id) => { setFilterOrgId(id); setCurrentPage(0) }}
                        orgOptions={orgOptions}
                        onFetchOrgOptions={fetchOrgOptions}
                        currentPage={currentPage}
                        onSetPage={setCurrentPage}
                        pageSize={pageSize}
                        totalItems={totalConversations}
                    />
                </div>

                {/* Content */}
                <div className='flex-lg-row-fluid ms-lg-7 ms-xl-10'>
                    <ChatWindow
                        activeConversation={currentConversation}
                        messages={messages}
                        loading={loadingMessages}
                        onSendMessage={sendMessage}
                        onSendMessageWithMedia={sendMessageWithMedia}
                        availableTags={availableTags}
                        onCreateTag={createTag}
                        onManageTag={manageTag}
                        configSource={configSource}
                        availableConfigs={availableConfigs}
                    />
                </div>
            </div>

            <NewChatModal
                show={showNewChat}
                onHide={() => setShowNewChat(false)}
                onSelect={handleContactSelected}
            />

            {selectedRecipient && (
                <SendMessageCanvas
                    show={showSendMessage}
                    onHide={() => setShowSendMessage(false)}
                    recipient={{
                        name: selectedRecipient.full_name,
                        phone: selectedRecipient.phone,
                        email: selectedRecipient.email
                    }}
                    context={{
                        type: 'CONTACT',
                        id: selectedRecipient.id
                    }}
                    onSuccess={handleMessageSent}
                />
            )}
        </>
    )
}

export { InboxPage }
